import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { BUDGETS, OCCASIONS, RECIPIENTS, VIBES } from "../../data/presents";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../lms/ui";
import { PopButton } from "../PopButton";

export interface AdminGift {
  id?: string; linked?: boolean; name: string; description: string; image_url: string; price: number | null;
  recipients: string[]; budget: string; vibes: string[]; occasions: string[];
  retailer: string; affiliate_network: string; tracking_url: string;
  availability: "in_stock" | "out_of_stock"; status: "active" | "archived"; clicks?: number;
  commission_rate: number | null; risk: "" | "green" | "amber" | "red"; approval: "" | "applied" | "approved" | "rejected"; last_checked: string;
}

export const EMPTY_GIFT: AdminGift = {
  name: "", description: "", image_url: "", price: null, recipients: [], budget: "20-50", vibes: [], occasions: [],
  retailer: "", affiliate_network: "", tracking_url: "", availability: "in_stock", status: "active",
  commission_rate: null, risk: "", approval: "", last_checked: "",
};

export const RISKS = [{ id: "green", label: "Green" }, { id: "amber", label: "Amber" }, { id: "red", label: "Red" }];
export const APPROVALS = [{ id: "applied", label: "Applied" }, { id: "approved", label: "Approved" }, { id: "rejected", label: "Rejected" }];

const label = "block text-[11px] font-extrabold uppercase tracking-wide text-[#6b3a12]";

function Checks({ title, options, value, onChange, testId }: { title: string; options: { id: string; label: string }[]; value: string[]; onChange: (v: string[]) => void; testId: string }) {
  return (
    <fieldset data-testid={testId}>
      <legend className={label}>{title}</legend>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {options.map((o) => {
          const on = value.includes(o.id);
          return (
            <button type="button" key={o.id} onClick={() => onChange(on ? value.filter((x) => x !== o.id) : [...value, o.id])} data-testid={`${testId}-${o.id}`}
              className={`rounded-full border-2 px-2.5 py-0.5 text-xs font-bold transition-colors ${on ? "border-[#1a1006] bg-[#FFE14D]" : "border-[#F2D9B8] bg-white text-[#6b3a12]"}`}>{o.label}</button>
          );
        })}
      </div>
    </fieldset>
  );
}

function Field({ name, value, onChange, testId, type = "text", placeholder }: { name: string; value: string; onChange: (v: string) => void; testId: string; type?: string; placeholder?: string }) {
  return (
    <label className={label}>{name}
      <input className="field mt-1 !py-2 !text-sm" type={type} step={type === "number" ? "0.01" : undefined} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </label>
  );
}

export function GiftForm({ gift, onClose, onSaved }: { gift: AdminGift; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState<AdminGift>({ ...EMPTY_GIFT, ...gift, risk: gift.risk || "", approval: gift.approval || "", last_checked: gift.last_checked || "" });
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof AdminGift>(k: K) => (v: AdminGift[K]) => setF((x) => ({ ...x, [k]: v }));
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const { id, clicks, ...body } = f;
    try {
      await (id ? api.put(`/admin/gifts/${id}`, body) : api.post("/admin/gifts", body));
      toast.success(id ? "Gift updated" : "Gift added");
      onSaved();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o: boolean) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="gift-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-comic text-2xl">{f.id ? "Edit gift" : "Add a gift"}</DialogTitle>
          <DialogDescription>Only active, in-stock gifts appear in the finder. Tracking links are never shown in the site code.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-2.5" data-testid="gift-form">
          <Field name="Product name *" value={f.name} onChange={set("name")} testId="gift-name-input" />
          <Field name="Description" value={f.description} onChange={set("description")} testId="gift-description-input" placeholder="One line on why it's a great gift" />
          <div className="grid gap-2 sm:grid-cols-2">
            <Field name="Image link (https://…)" value={f.image_url} onChange={set("image_url")} testId="gift-image-input" />
            <Field name="Price (£)" type="number" value={f.price == null ? "" : String(f.price)} onChange={(v) => set("price")(v === "" ? null : Number(v))} testId="gift-price-input" />
          </div>
          <label className={label}>Budget category *
            <select className="field mt-1 !py-2 !text-sm" value={f.budget} onChange={(e) => set("budget")(e.target.value)} data-testid="gift-budget-select">
              {BUDGETS.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
            </select>
          </label>
          <Checks title="Recipient *" options={RECIPIENTS} value={f.recipients} onChange={set("recipients")} testId="gift-recipients" />
          <Checks title="Vibe / personality *" options={VIBES} value={f.vibes} onChange={set("vibes")} testId="gift-vibes" />
          <Checks title="Occasion" options={OCCASIONS} value={f.occasions} onChange={set("occasions")} testId="gift-occasions" />
          <div className="grid gap-2 sm:grid-cols-2">
            <Field name="Retailer" value={f.retailer} onChange={set("retailer")} testId="gift-retailer-input" placeholder="e.g. Amazon" />
            <Field name="Product source / network" value={f.affiliate_network} onChange={set("affiliate_network")} testId="gift-network-input" placeholder="e.g. Awin" />
          </div>
          <Field name="Affiliate tracking URL (https://…)" value={f.tracking_url} onChange={set("tracking_url")} testId="gift-tracking-input" />
          <div className="grid gap-2 sm:grid-cols-4">
            <Field name="Commission (%)" type="number" value={f.commission_rate == null ? "" : String(f.commission_rate)} onChange={(v) => set("commission_rate")(v === "" ? null : Number(v))} testId="gift-commission-input" />
            <label className={label}>Payment risk
              <select className="field mt-1 !py-2 !text-sm" value={f.risk} onChange={(e) => set("risk")(e.target.value as AdminGift["risk"])} data-testid="gift-risk-select">
                <option value="">Not set</option>{RISKS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </label>
            <label className={label}>Programme approval
              <select className="field mt-1 !py-2 !text-sm" value={f.approval} onChange={(e) => set("approval")(e.target.value as AdminGift["approval"])} data-testid="gift-approval-select">
                <option value="">Not set</option>{APPROVALS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </label>
            <Field name="Last checked" type="date" value={f.last_checked} onChange={set("last_checked")} testId="gift-last-checked-input" />
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <label className={label}>Availability
              <select className="field mt-1 !py-2 !text-sm" value={f.availability} onChange={(e) => set("availability")(e.target.value as AdminGift["availability"])} data-testid="gift-availability-select">
                <option value="in_stock">In stock</option><option value="out_of_stock">Out of stock</option>
              </select>
            </label>
            <label className={label}>Status
              <select className="field mt-1 !py-2 !text-sm" value={f.status} onChange={(e) => set("status")(e.target.value as AdminGift["status"])} data-testid="gift-status-select">
                <option value="active">Active</option><option value="archived">Archived</option>
              </select>
            </label>
          </div>
          <div className="flex gap-2 pt-1">
            <PopButton type="submit" tone="orange" disabled={busy || !f.name.trim() || !f.recipients.length || !f.vibes.length} data-testid="gift-save-button">{busy ? "Saving…" : "Save gift"}</PopButton>
            <PopButton type="button" tone="white" onClick={onClose} data-testid="gift-cancel-button">Cancel</PopButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
